import {
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
  useMemo,
  memo,
  forwardRef,
} from 'react';
import * as Yup from 'yup';
import clsx from 'clsx';
import { textFieldValidation } from '@/utils/validations';
import { KeenIcon, Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import Select, { MultiValue } from 'react-select';
import type { SelectInstance } from 'react-select';
import { useSnackbar } from 'notistack';
import {
  CommunityStatus,
  ICommunityData,
  ICommunityTranslation,
  IModerator,
} from '@/types/community-management.types';
import { CommunityUserResponse } from '@/types/user-management.types';
import { useGetCommunityUserQuery } from '@/services/CommunityUsers';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import { useIntl, IntlShape } from 'react-intl';
import CommunityPreviewModal from './CommunityPreviewModal';
import { useTranslateCommunityContentMutation } from '@/services/CommunityManagementService';
import NumberInput from '@/components/inputControls/NumberInput';
import { resolveApiMessageFromError } from '@/utils/apiMessageUtils';

interface SelectOption {
  value: string;
  label: string;
  email: string;
}

interface CommunityModalProps extends Omit<
  ModalProps,
  'children' | 'onSubmit'
> {
  mode: 'view' | 'edit' | 'create';
  formData: Partial<ICommunityData>;
  setFormData: Dispatch<SetStateAction<Partial<ICommunityData>>>;
  errors: { [key: string]: string };
  setErrors: Dispatch<SetStateAction<{ [key: string]: string }>>;
  onSubmit: (data: Partial<ICommunityData>) => Promise<void>;
  isSubmitting?: boolean;
}

const hasTranslations = (
  value?: Record<string, ICommunityTranslation> | null
): value is Record<string, ICommunityTranslation> =>
  Boolean(value && Object.keys(value).length > 0);

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const getCommunityValidationSchema = (
  mode: CommunityModalProps['mode'],
  intl: IntlShape
) =>
  Yup.object().shape({
    moderator_ids:
      mode === 'create'
        ? Yup.array()
            .of(Yup.string().required())
            .min(
              1,
              intl.formatMessage({
                id: 'COMMUNITY.VALIDATION.MODERATOR_REQUIRED',
              })
            )
        : Yup.array(),
    name: textFieldValidation(
      intl.formatMessage({ id: 'COMMUNITY.FORM.NAME' }),
      2,
      50,
      true,
      intl
    ),
    description: textFieldValidation(
      intl.formatMessage({ id: 'COMMON.DESCRIPTION' }),
      0,
      500,
      false,
      intl
    ),
    status: Yup.string()
      .required(
        intl.formatMessage(
          { id: 'VALIDATION.REQUIRED' },
          { field: intl.formatMessage({ id: 'COMMON.STATUS' }) }
        )
      )
      .oneOf(
        Object.values(CommunityStatus),
        intl.formatMessage({ id: 'COMMUNITY.VALIDATION.STATUS_INVALID' })
      ),
    tags: Yup.array()
      .of(
        Yup.string().required(
          intl.formatMessage({ id: 'COMMUNITY.VALIDATION.TAG_STRING' })
        )
      )
      .notRequired()
      .nullable(),
    max_users: Yup.number()
      .min(1, intl.formatMessage({ id: 'COMMUNITY.VALIDATION.MAX_USERS_MIN' }))
      .optional()
      .nullable(),
  });

const CommunityModal = ({
  mode,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  open,
  onClose,
  className,
}: CommunityModalProps) => {
  const [selectedModerators, setSelectedModerators] = useState<IModerator[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [tagInput, setTagInput] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { enqueueSnackbar } = useSnackbar();
  const { currentLanguage, changeLanguage } = useLanguage();
  const intl = useIntl();
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [translations, setTranslations] = useState<
    Record<string, ICommunityTranslation>
  >({});
  const [formDataSnapshot, setFormDataSnapshot] = useState<
    Pick<Partial<ICommunityData>, 'name' | 'description' | 'tags'>
  >({});
  const [translationsValid, setTranslationsValid] = useState(false);
  const [translateCommunityContent] = useTranslateCommunityContentMutation();
  const previewInitialTranslations = useMemo(
    () =>
      hasTranslations(translations)
        ? translations
        : formData.translations || {},
    [formData.translations, translations]
  );

  // Focus the Community Admin select when the modal opens (create/edit)
  const adminSelectRef = useRef<SelectInstance<SelectOption> | null>(null);

  const hasHadErrorsRef = useRef(false);

  useEffect(() => {
    if (!open) {
      hasHadErrorsRef.current = false;
      return;
    }

    if (Object.keys(errors).length) {
      hasHadErrorsRef.current = true;
    }
  }, [errors, open]);

  const validateField = useCallback(
    async (
      name: keyof ICommunityData,
      value: string | number | string[] | undefined
    ) => {
      try {
        await getCommunityValidationSchema(mode, intl).validateAt(name, {
          ...formData,
          [name]: value,
        });
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => {
            const newError =
              err.errors[0] ||
              intl.formatMessage({ id: 'VALIDATION.INVALID_INPUT' });
            if (prev[name] === newError) return prev;
            return { ...prev, [name]: newError };
          });
        }
      }
    },
    [setErrors, formData, mode, intl]
  );

  const { data: communityUsersResponse, isLoading: isUsersLoading } =
    useGetCommunityUserQuery(
      {
        page: 1,
        limit: 100,
        search: debouncedSearchTerm || undefined,
      },
      { skip: !open }
    );

  const moderatorOptions = useMemo<SelectOption[]>(() => {
    if (!communityUsersResponse?.data) return [];
    const users = communityUsersResponse.data;
    if (!Array.isArray(users)) return [];
    return users
      .filter((user: CommunityUserResponse) => user.name || user.email)
      .map((user: CommunityUserResponse) => ({
        value: user.id,
        label: user.name || user.email || '',
        email: user.email,
      }));
  }, [communityUsersResponse]);

  const handleModeratorChange = (selectedOptions: MultiValue<SelectOption>) => {
    if (!selectedOptions) {
      setSelectedModerators([]);
      setFormData((prev) => ({
        ...prev,
        moderators: [],
        moderator_ids: [],
      }));
      return;
    }

    const options = selectedOptions || [];
    const newSelectedModerators = options.map((option) => ({
      id: option.value,
      name: option.label,
      email: option.email,
    }));

    setSelectedModerators(newSelectedModerators);
    setFormData((prev) => ({
      ...prev,
      moderators: newSelectedModerators,
      moderator_ids: newSelectedModerators.map((m) => m.id),
    }));
  };

  // Initialize form data when modal opens
  const isInitialized = useRef(false);
  useEffect(() => {
    if (!open) {
      isInitialized.current = false;
      // The modal stays mounted, so drop the previous community's admin
      setSelectedModerators([]);
      prevSelectedModerators.current = [];
      return;
    }

    // Only initialize once when opening the modal
    if (!isInitialized.current) {
      isInitialized.current = true;

      if (mode === 'create') {
        const newFormData = {
          status: CommunityStatus.GLOBAL,
          tags: [],
          max_users: undefined,
          moderators: [],
          moderator_ids: [],
        };
        setFormData((prev) => ({ ...prev, ...newFormData }));
        setSelectedModerators([]);
        setTranslations({});
        setTranslationsValid(false);
        setFormDataSnapshot({});
      } else if (mode === 'edit' || mode === 'view') {
        if (formData.moderators?.length) {
          setSelectedModerators(formData.moderators);
        } else if (formData.moderator_id && formData.moderator) {
          setSelectedModerators([
            {
              id: formData.moderator_id,
              name: formData.moderator.name,
              email: formData.moderator.email || '',
            },
          ]);
        } else {
          setSelectedModerators([]);
        }

        if (hasTranslations(formData.translations)) {
          setTranslations(formData.translations);
          setTranslationsValid(true);
          setFormDataSnapshot({
            name: formData.name,
            description: formData.description,
            tags: formData.tags,
          });
        }
      }
    }
  }, [open, mode, formData, setFormData]);

  useEffect(() => {
    if (!open) return;
    if (formData.language) return;

    setFormData((prev) => ({
      ...prev,
      language: currentLanguage.code || 'en',
    }));
  }, [open, formData.language, currentLanguage.code, setFormData]);

  useEffect(() => {
    if (!open || mode === 'create') return;

    if (hasTranslations(formData.translations)) {
      setTranslations(formData.translations);
      setTranslationsValid(true);
      setFormDataSnapshot({
        name: formData.name,
        description: formData.description,
        tags: formData.tags,
      });
    } else {
      setTranslations({});
      setTranslationsValid(false);
      setFormDataSnapshot({});
    }
  }, [
    open,
    mode,
    formData.id,
    formData.name,
    formData.description,
    formData.tags,
    formData.translations,
  ]);

  const allModeratorOptions = useMemo(() => {
    const options = [...moderatorOptions];
    selectedModerators.forEach((m) => {
      if (!options.some((o) => o.value === m.id)) {
        options.push({
          value: m.id,
          label: m.name,
          email: m.email || '',
        });
      }
    });
    return options;
  }, [moderatorOptions, selectedModerators]);

  // Memoize the selected values for the Select component
  const selectedValues = useMemo(() => {
    return allModeratorOptions.filter((option) =>
      selectedModerators.some((m) => m.id === option.value)
    );
  }, [allModeratorOptions, selectedModerators]);

  // Update form data when selected moderators change
  const prevSelectedModerators = useRef(selectedModerators);

  useEffect(() => {
    if (!open) return;

    // Only update if moderators actually changed
    if (
      JSON.stringify(prevSelectedModerators.current) !==
      JSON.stringify(selectedModerators)
    ) {
      prevSelectedModerators.current = selectedModerators;

      setFormData((prev) => ({
        ...prev,
        moderators: selectedModerators,
        moderator_ids: selectedModerators.map((m) => m.id),
      }));
    }
  }, [selectedModerators, open, setFormData]);

  // The full community is fetched after the modal opens; pick up its moderators
  useEffect(() => {
    if (!open || mode === 'create' || !formData.moderators?.length) return;

    const incomingIds = formData.moderators.map((m) => m.id).join(',');
    const currentIds = selectedModerators.map((m) => m.id).join(',');
    if (incomingIds !== currentIds) {
      prevSelectedModerators.current = formData.moderators;
      setSelectedModerators(formData.moderators);
    }
  }, [open, mode, formData.moderators, selectedModerators]);

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;
      const key = name as keyof ICommunityData;
      const parsedValue =
        name === 'max_users'
          ? value === ''
            ? undefined
            : parseInt(value, 10)
          : value;

      setFormData((prev: Partial<ICommunityData>) => ({
        ...prev,
        [key]: parsedValue,
      }));

      validateField(key, parsedValue);
    },
    [setFormData, validateField]
  );

  const handleOpenPreview = useCallback(() => {
    setIsPreviewModalOpen(true);
  }, []);

  const handleTranslationsGenerated = useCallback(
    (generatedTranslations: Record<string, ICommunityTranslation>) => {
      setTranslations(generatedTranslations);
      setFormDataSnapshot({
        name: formData.name,
        description: formData.description,
        tags: formData.tags,
      });
      setTranslationsValid(true);
    },
    [formData.name, formData.description, formData.tags]
  );

  useEffect(() => {
    if (!translationsValid) return;

    const hasChanged =
      formData.name !== formDataSnapshot.name ||
      formData.description !== formDataSnapshot.description ||
      JSON.stringify(formData.tags || []) !==
        JSON.stringify(formDataSnapshot.tags || []);

    if (hasChanged) {
      setTranslationsValid(false);
    }
  }, [
    formData.name,
    formData.description,
    formData.tags,
    formDataSnapshot,
    translationsValid,
  ]);

  const handleInputBlur = useCallback(
    (name: string, value: unknown) => {
      validateField(name as keyof ICommunityData, value as string);
    },
    [validateField]
  );

  const handleTagAdd = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      const newTags = [...(formData.tags || []), tagInput.trim()];
      setFormData((prev) => ({ ...prev, tags: newTags }));
      validateField('tags', newTags);
      setTagInput('');
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    const newTags = (formData.tags || []).filter((tag) => tag !== tagToRemove);
    setFormData((prev) => ({ ...prev, tags: newTags }));
    validateField('tags', newTags);
  };

  const handleClearAllTags = () => {
    setFormData((prev) => ({ ...prev, tags: [] }));
    validateField('tags', []);
  };

  const handleSubmit = useCallback(async () => {
    try {
      // Then validate the rest of the form
      await getCommunityValidationSchema(mode, intl).validate(formData, {
        abortEarly: false,
      });

      // Clear any existing errors
      setErrors({});

      // Ensure translations are present: generate if missing
      let translationsToUse = translations;
      if (
        !hasTranslations(translationsToUse) &&
        (formData.description || (formData.tags && formData.tags.length > 0))
      ) {
        try {
          const payload = {
            description: formData.description,
            tags: formData.tags,
            source_language: formData.language || currentLanguage.code || 'en',
            community_id: formData.id ? String(formData.id) : undefined,
          };
          const response = await translateCommunityContent(payload).unwrap();
          if (response && response.translations) {
            translationsToUse = response.translations as typeof translations;
            setTranslations(translationsToUse);
            setFormDataSnapshot({
              name: formData.name,
              description: formData.description,
              tags: formData.tags,
            });
            setTranslationsValid(true);

            const warningMessage = (response as { warning_message?: unknown })
              ?.warning_message;
            const message = (response as { message?: string })?.message;

            if (typeof warningMessage === 'string' && warningMessage.trim()) {
              enqueueSnackbar(warningMessage, { variant: 'warning' });
            } else if (message) {
              enqueueSnackbar(message, { variant: 'success' });
            }
          }
        } catch (error) {
          enqueueSnackbar(
            resolveApiMessageFromError(
              error,
              intl,
              intl.formatMessage({ id: 'POI.PREVIEW.TRANSLATION_ERROR' })
            ),
            { variant: 'warning' }
          );
        }
      }

      // Submit if validation passes
      await onSubmit({
        ...formData,
        translations: translationsToUse,
      });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: Record<string, string> = {};

        // Handle other validation errors
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
      } else {
        enqueueSnackbar(intl.formatMessage({ id: 'COMMUNITY.ERROR.SAVE' }), {
          variant: 'error',
        });
      }
    }
  }, [
    formData,
    onSubmit,
    setErrors,
    mode,
    enqueueSnackbar,
    intl,
    translations,
    currentLanguage.code,
    translateCommunityContent,
  ]);

  useEffect(() => {
    if (!open) return;
    if (!hasHadErrorsRef.current) return;

    (async () => {
      try {
        await getCommunityValidationSchema(mode, intl).validate(formData, {
          abortEarly: false,
        });
        setErrors((prev) => {
          if (!Object.keys(prev).length) return prev;
          return {};
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const newErrors: Record<string, string> = {};
          err.inner.forEach((error) => {
            if (error.path) {
              newErrors[error.path] = error.message;
            }
          });

          setErrors((prev) => {
            const prevStr = JSON.stringify(prev);
            const nextStr = JSON.stringify(newErrors);
            if (prevStr === nextStr) return prev;
            return newErrors;
          });
        }
      }
    })();
  }, [currentLanguage.code, intl, mode, formData, open, setErrors]);

  // Autofocus the Community Admin when modal opens and options are ready
  useEffect(() => {
    if (open && mode !== 'view' && !isUsersLoading) {
      const t = setTimeout(() => adminSelectRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [open, mode, isUsersLoading]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx('!flex items-center justify-center', className)}
      aria-labelledby="community-modal-title"
      aria-describedby="community-modal-description"
      data-testid="community-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-gray-100 p-8 w-full max-w-2xl mx-4 rounded-xl shadow-2xl flex flex-col responsive-form community-modal max-h-[90vh] overflow-hidden"
        role="document"
      >
        <div className="flex justify-between items-center mb-6">
          <h1
            id="community-modal-title"
            className="text-2xl font-semibold text-gray-800"
          >
            {mode === 'create'
              ? intl.formatMessage({ id: 'COMMUNITY.FORM.CREATE.TITLE' })
              : mode === 'edit'
                ? intl.formatMessage({ id: 'COMMUNITY.FORM.EDIT.TITLE' })
                : intl.formatMessage({ id: 'COMMUNITY.FORM.VIEW.TITLE' })}
          </h1>
          <div className="relative">
            <select
              id="community-language-select"
              className="select select-bordered pl-8 pr-8 h-10 leading-normal"
              value={currentLanguage.code}
              onChange={(e) => {
                const selectedLang = I18N_LANGUAGES.find(
                  (lang: { code: string; label: string }) =>
                    lang.code === e.target.value
                );
                if (selectedLang) {
                  changeLanguage(selectedLang);
                  setFormData((prev) => ({
                    ...prev,
                    language: selectedLang.code,
                  }));
                }
              }}
              aria-label={intl.formatMessage({
                id: 'COMMUNITY.FORM.SELECT_LANGUAGE',
              })}
            >
              {I18N_LANGUAGES.map((lang: { code: string; label: string }) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
            <img
              src="/media/flags/globe.svg"
              alt="Globe"
              className="absolute dark:brightness-0 dark:invert contrast:brightness-0 contrast:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>
        <div id="community-modal-description" className="sr-only">
          {mode === 'create'
            ? intl.formatMessage({
                id: 'COMMUNITY.FORM.CREATE.DESCRIPTION',
              })
            : mode === 'edit'
              ? intl.formatMessage({
                  id: 'COMMUNITY.FORM.EDIT.DESCRIPTION',
                })
              : intl.formatMessage({
                  id: 'COMMUNITY.FORM.VIEW.DESCRIPTION',
                })}
        </div>
        <div
          className="flex-1 overflow-y-auto max-h-[70vh] pr-2 custom-scrollbar"
          role="form"
          aria-label={intl.formatMessage({ id: 'COMMUNITY.FORM.ARIA_LABEL' })}
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
            {/* Moderator */}
            <div className="col-span-1">
              <label
                htmlFor="community-admin-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'COMMUNITY.FORM.ADMIN' })}{' '}
                {mode === 'create' && (
                  <span className="text-red-500" aria-label="required">
                    *
                  </span>
                )}
              </label>
              <MemoizedSelect
                ref={adminSelectRef}
                options={allModeratorOptions}
                selectedValues={selectedValues}
                onChange={handleModeratorChange}
                isDisabled={
                  mode === 'view' || isUsersLoading || (isSubmitting ?? false)
                }
                isLoading={isUsersLoading}
                error={!!errors.moderator_ids}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                inputId="community-admin-input"
                ariaDescribedBy={
                  errors.moderator_ids ? 'moderator_ids-error' : undefined
                }
                autoFocus={open && mode !== 'view' && !isUsersLoading}
              />
              {errors.moderator_ids && (
                <p
                  id="moderator_ids-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.moderator_ids}
                </p>
              )}
            </div>

            {/* Name */}
            <div className="col-span-1">
              <label
                htmlFor="community-name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'COMMUNITY.FORM.NAME' })}{' '}
                <span className="text-red-500" aria-label="required">
                  *
                </span>
              </label>
              <input
                id="community-name"
                type="text"
                name="name"
                data-testid="community-name-input"
                value={formData.name ?? ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                required={mode !== 'view'}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.name && 'border-red-500'
                )}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
                aria-required="true"
                placeholder={intl.formatMessage({
                  id: 'COMMUNITY.FORM.NAME.PLACEHOLDER',
                })}
                autoComplete="off"
              />
              {errors.name && (
                <p
                  id="name-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.name}
                </p>
              )}
            </div>

            {/* Tags */}
            <div className="col-span-2">
              <label
                htmlFor="tags-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'COMMUNITY.FORM.TAGS' })}
              </label>
              <div className="flex gap-2 mb-2">
                <div className="relative flex-1">
                  <input
                    id="tags-input"
                    name="tags"
                    data-testid="tags-input"
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && (e.preventDefault(), handleTagAdd())
                    }
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300 pr-20',
                      errors.tags && 'border-red-500'
                    )}
                    placeholder={intl.formatMessage({
                      id: 'COMMUNITY.FORM.TAGS.PLACEHOLDER',
                    })}
                    aria-label={intl.formatMessage({
                      id: 'COMMUNITY.FORM.TAGS.PLACEHOLDER',
                    })}
                    aria-invalid={!!errors.tags}
                    autoComplete="off"
                  />
                  {(formData.tags || []).length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAllTags}
                      disabled={mode === 'view' || isSubmitting}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                      title={intl.formatMessage({
                        id: 'COMMUNITY.FORM.TAGS.CLEAR_ALL',
                      })}
                      aria-label={intl.formatMessage({
                        id: 'COMMUNITY.FORM.TAGS.CLEAR_ALL',
                      })}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleTagAdd}
                  disabled={!tagInput.trim() || mode === 'view' || isSubmitting}
                  className="btn btn-sm h-11 btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2"
                  aria-label="Add tag"
                >
                  {intl.formatMessage({ id: 'COMMUNITY.FORM.TAGS.ADD' })}
                </button>
              </div>
              <div
                className="flex flex-wrap gap-2"
                role="list"
                aria-label={intl.formatMessage({
                  id: 'COMMUNITY.FORM.TAGS.SELECTED_ARIA',
                })}
              >
                {(formData.tags || []).map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center bg-blue-100 dark:bg-blue-900 contrast:bg-blue-700 text-blue-800 dark:text-blue-200 contrast:text-white text-sm font-medium px-2.5 py-0.5 rounded space-x-[2px]"
                    role="listitem"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleTagRemove(tag)}
                      disabled={mode === 'view' || isSubmitting}
                      className="text-blue-800 hover:text-blue-900"
                      aria-label={intl.formatMessage(
                        { id: 'COMMON.REMOVE_ITEM' },
                        { label: tag }
                      )}
                    >
                      <KeenIcon
                        type="filled"
                        icon="cross"
                        className="w-3 h-3"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                ))}
              </div>
              {errors.tags && (
                <p className="text-red-500 text-xs mt-1" role="alert">
                  {errors.tags}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="col-span-2">
              <label
                htmlFor="community-description"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'COMMON.DESCRIPTION' })}
              </label>
              <textarea
                id="community-description"
                name="description"
                data-testid="community-description-input"
                value={formData.description ?? ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                autoComplete="off"
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'textarea textarea-bordered w-full h-32 rounded-lg border-gray-300',
                  errors.description && 'border-red-500'
                )}
                aria-invalid={!!errors.description}
                aria-describedby={
                  errors.description ? 'description-error' : undefined
                }
                placeholder={intl.formatMessage({
                  id: 'COMMUNITY.FORM.DESCRIPTION.PLACEHOLDER',
                })}
              />
              {errors.description && (
                <p
                  id="description-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.description}
                </p>
              )}
            </div>

            {/* Status */}
            <div className="col-span-1">
              <label
                htmlFor="status"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'COMMON.STATUS' })}{' '}
                <span className="text-red-500" aria-label="required">
                  *
                </span>
              </label>
              <select
                id="status"
                name="status"
                value={formData.status || ''}
                onChange={handleInputChange}
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.status && 'border-red-500'
                )}
                disabled={mode === 'view' || isSubmitting}
                required={mode !== 'view'}
                aria-invalid={!!errors.status}
                aria-describedby={errors.status ? 'status-error' : undefined}
                aria-required="true"
              >
                <option value="">
                  {intl.formatMessage({ id: 'COMMUNITY.FORM.STATUS.SELECT' })}
                </option>
                <option value="PRIVATE">
                  {intl.formatMessage({ id: 'COMMUNITY.FORM.STATUS.PRIVATE' })}
                </option>
                <option value="GLOBAL">
                  {intl.formatMessage({ id: 'COMMUNITY.FORM.STATUS.GLOBAL' })}
                </option>
              </select>
              {errors.status && (
                <p
                  id="status-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.status}
                </p>
              )}
            </div>

            {/* Max Users */}
            <div className="col-span-1">
              <label
                htmlFor="max-users-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'COMMUNITY.FORM.MAX_USERS' })}
              </label>
              <NumberInput
                id="max-users-input"
                name="max_users"
                data-testid="max-users-input"
                value={formData.max_users ?? ''}
                onChange={handleInputChange}
                onBlur={(e) =>
                  handleInputBlur(
                    e.target.name,
                    e.target.value === ''
                      ? undefined
                      : parseInt(e.target.value, 10)
                  )
                }
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                  errors.max_users && 'border-red-500'
                )}
                aria-invalid={!!errors.max_users}
                aria-describedby={
                  errors.max_users ? 'max-users-error' : undefined
                }
                placeholder={intl.formatMessage({
                  id: 'COMMUNITY.FORM.MAX_USERS.PLACEHOLDER',
                })}
                min="1"
                autoComplete="off"
              />
              {errors.max_users && (
                <p
                  id="max-users-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.max_users}
                </p>
              )}
            </div>
          </div>
          {(mode === 'create' || mode === 'edit') && formData.name && (
            <div className="mt-4 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-700 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-lg p-3 flex items-center justify-between gap-2 community-preview-notice">
              <div className="flex items-center">
                <KeenIcon
                  type="outline"
                  icon="paper-clip"
                  className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2"
                />
                <p className="text-blue-700 dark:text-blue-400 contrast:text-white text-xs community-preview-notice-text">
                  {intl.formatMessage({ id: 'COMMUNITY.PREVIEW.NOTICE' })}
                </p>
              </div>
              <button
                type="button"
                onClick={handleOpenPreview}
                className="text-nowrap btn btn-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 community-preview-notice-button"
                aria-label={intl.formatMessage({
                  id: 'COMMUNITY.BUTTON.SHOW_PREVIEW',
                })}
                disabled={isSubmitting}
              >
                {intl.formatMessage({ id: 'COMMUNITY.BUTTON.SHOW_PREVIEW' })}
              </button>
            </div>
          )}
        </div>
        {(mode === 'create' || mode === 'edit') && (
          <div
            className="mt-8 flex justify-end gap-3 sticky bottom-0 bg-gray-100 pt-3 border-t z-10"
            role="group"
            aria-label="Form actions"
          >
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="btn btn-sm btn-ghost text-gray-600 hover:bg-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
              aria-label={intl.formatMessage({
                id: 'COMMUNITY.FORM.CANCEL.ARIA',
              })}
              disabled={isSubmitting}
            >
              {intl.formatMessage({ id: 'COMMON.CANCEL' })}
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="btn btn-sm btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={
                mode === 'create'
                  ? intl.formatMessage({
                      id: 'COMMUNITY.FORM.SUBMIT.CREATE.ARIA',
                    })
                  : intl.formatMessage({
                      id: 'COMMUNITY.FORM.SUBMIT.SAVE.ARIA',
                    })
              }
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              data-testid="modal-submit-button"
            >
              {isSubmitting
                ? intl.formatMessage({ id: 'COMMUNITY.BUTTON.SAVING' })
                : mode === 'create'
                  ? intl.formatMessage({ id: 'COMMON.CREATE' })
                  : intl.formatMessage({ id: 'COMMON.SAVE' })}
            </button>
          </div>
        )}
        {isPreviewModalOpen && (
          <CommunityPreviewModal
            open={isPreviewModalOpen}
            onClose={() => setIsPreviewModalOpen(false)}
            formData={formData}
            source_language={formData.language || currentLanguage.code}
            initialTranslations={previewInitialTranslations}
            onTranslationsGenerated={handleTranslationsGenerated}
          />
        )}
      </div>
    </Modal>
  );
};

// Memoized Select component to prevent unnecessary re-renders
const MemoizedSelect = memo(
  forwardRef<
    SelectInstance<SelectOption>,
    {
      options: SelectOption[];
      selectedValues: SelectOption[];
      onChange: (value: MultiValue<SelectOption>) => void;
      isDisabled: boolean;
      isLoading: boolean;
      error: boolean;
      searchTerm: string;
      onSearchChange: (value: string) => void;
      inputId?: string;
      ariaDescribedBy?: string;
      autoFocus?: boolean;
    }
  >(
    (
      {
        options,
        selectedValues,
        onChange,
        isDisabled,
        isLoading,
        error,
        searchTerm,
        onSearchChange,
        inputId,
        ariaDescribedBy,
        autoFocus,
      },
      ref
    ) => {
      const intl = useIntl();

      return (
        <Select
          ref={ref}
          id="moderator-ids"
          inputId={inputId}
          aria-describedby={ariaDescribedBy}
          aria-invalid={error}
          autoFocus={autoFocus}
          isMulti
          options={options}
          value={selectedValues}
          onChange={(newValue) =>
            onChange((newValue as MultiValue<SelectOption>) ?? [])
          }
          onInputChange={onSearchChange}
          isDisabled={isDisabled}
          className={clsx('basic-multi-select', error ? 'border-red-500' : '')}
          classNamePrefix="select"
          placeholder={
            isLoading
              ? intl.formatMessage({ id: 'COMMUNITY.FORM.ADMIN.LOADING' })
              : intl.formatMessage({ id: 'COMMUNITY.FORM.ADMIN.SELECT' })
          }
          aria-label={intl.formatMessage({ id: 'COMMUNITY.FORM.ADMIN.ARIA' })}
          noOptionsMessage={() =>
            searchTerm
              ? intl.formatMessage({ id: 'COMMUNITY.FORM.ADMIN.NOT_FOUND' })
              : intl.formatMessage({ id: 'COMMUNITY.FORM.ADMIN.NONE' })
          }
          loadingMessage={() =>
            intl.formatMessage({ id: 'COMMON.LOADING_OPTIONS' })
          }
          formatOptionLabel={(option: SelectOption) => (
            <div className="flex flex-col">
              <span>{option.label}</span>
            </div>
          )}
        />
      );
    }
  )
);

MemoizedSelect.displayName = 'MemoizedSelect';

export { CommunityModal };
